// src/components/Feed/EditPost.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
  Chip,
  Avatar,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Photo,
  Videocam,
  Link as LinkIcon,
  EmojiEmotions,
  Close,
  LocationOn,
  Public,
  People,
  Lock,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { updatePost, uploadFile, getFileUrl, deleteFile } from '../../services/api';
import S3Avatar from '../Common/S3Avatar';
import { v4 as uuidv4 } from 'uuid';

const EditPost = ({ post, open, onClose, onPostUpdated }) => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [content, setContent] = useState('');
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState(null);
  const [links, setLinks] = useState([]);
  const [hashtags, setHashtags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [visibility, setVisibility] = useState('FOLLOWERS');
  const [location, setLocation] = useState('');
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [mediaUrls, setMediaUrls] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  
  const fileInputRef = useRef();
  const videoInputRef = useRef();
  const textFieldRef = useRef();

  const emojiCategories = {
    'Faces': ['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳'],
    'Gestures': ['👍', '👎', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✊', '👊', '🤛', '🤜'],
    'Hearts': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟'],
    'Entertainment': ['🎬', '🎭', '🎪', '🎨', '🎤', '🎧', '🎼', '🎵', '🎶', '🎹', '🥁', '🎷', '🎺', '🎸', '🪕', '🎻', '🎲', '♠️', '♥️', '♦️', '♣️', '🃏', '🀄', '🎯'],
    'Food': ['🍿', '🍕', '🍔', '🌭', '🥪', '🌮', '🌯', '🥙', '🧆', '🥚', '🍳', '🥘', '🍲', '🥗', '🍜', '🍝', '🍠', '🍢', '🍣', '🍤', '🍙', '🍚', '🍘', '🍥'],
    'Objects': ['📱', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '🖲️', '💽', '💾', '💿', '📀', '📼', '📷', '📸', '📹', '🎥', '📞', '☎️', '📟', '📠', '📺', '📻', '🎙️', '🎚️']
  };

  const visibilityOptions = [
    { value: 'PUBLIC', label: 'Public', icon: <Public fontSize="small" />, description: 'Anyone can see this post' },
    { value: 'FOLLOWERS', label: 'Followers', icon: <People fontSize="small" />, description: 'Only your followers can see this' },
    { value: 'PRIVATE', label: 'Only Me', icon: <Lock fontSize="small" />, description: 'Only you can see this post' },
  ];

  // Initialize form with post data
  useEffect(() => {
    if (post && open) {
      setContent(post.content || '');
      setHashtags(post.hashtags || []);
      setLinks(post.links || []);
      setVisibility(post.visibility || 'FOLLOWERS');
      setLocation(''); // Location not stored in post, reset
      
      // Load existing media
      if (post.images && post.images.length > 0) {
        loadExistingMedia();
      } else {
        setImages([]);
        setMediaUrls([]);
      }
      
      if (post.video) {
        setVideo({ key: post.video, url: post.video, name: 'Existing Video', existing: true });
      } else {
        setVideo(null);
      }
    }
  }, [post, open]);

  const loadExistingMedia = async () => {
    if (!post.images || post.images.length === 0) return;
    
    try {
      setMediaLoading(true);
      const authorIdentityId = post.authorIdentityId || post.author?.identityId;
      const isPublicPost = post.visibility === 'PUBLIC';
      
      let accessLevel = 'protected';
      let targetIdentityId = authorIdentityId;
      
      if (isPublicPost) {
        accessLevel = 'public';
        targetIdentityId = null;
      }
      
      const urls = await Promise.all(
        post.images.map(async (key) => {
          const url = await getFileUrl(key, targetIdentityId, accessLevel);
          return { key, url, name: 'Existing Image', existing: true };
        })
      );
      
      setImages(urls.filter(item => item.url !== null));
      setMediaUrls(urls.map(item => item.url).filter(url => url !== null));
    } catch (error) {
      console.error("Error loading existing media:", error);
      setImages([]);
      setMediaUrls([]);
    } finally {
      setMediaLoading(false);
    }
  };

  const iconButtonStyle = {
    minWidth: { xs: 44, sm: 'auto' },
    minHeight: { xs: 44, sm: 'auto' },
    color: theme.palette.mode === 'dark' ? '#ffd700' : '#1976d2',
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 215, 0, 0.1)' 
      : 'rgba(25, 118, 210, 0.08)',
    border: theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 215, 0, 0.2)'
      : '1px solid rgba(25, 118, 210, 0.2)',
    borderRadius: 2,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255, 215, 0, 0.2)'
        : 'rgba(25, 118, 210, 0.12)',
      transform: 'scale(1.05)',
    },
    '&:disabled': {
      color: theme.palette.mode === 'dark' ? '#666' : '#ccc',
      backgroundColor: 'transparent',
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length + images.length > 5) {
      addNotification({
        type: 'warning',
        message: 'Maximum 5 images allowed per post for better performance',
      });
      return;
    }
    
    try {
      setLoading(true);
      const uploadPromises = files.map(async (file) => {
        if (file.size > 10 * 1024 * 1024) {
          throw new Error(`Image ${file.name} is too large. Maximum size is 10MB.`);
        }
        
        const key = `posts/${user.username}/${uuidv4()}-${file.name}`;
        const accessLevel = visibility === 'PUBLIC' ? 'public' : 'protected';
        const uploadedKey = await uploadFile(file, key, (progress) => {
          setUploadProgress(progress);
        }, accessLevel);
        const accessibleUrl = await getFileUrl(uploadedKey, null, accessLevel);
        return {
          key: uploadedKey,
          url: accessibleUrl,
          name: file.name,
          size: file.size,
          existing: false,
        };
      });
      
      const uploadedFiles = await Promise.all(uploadPromises);
      setImages(prev => [...prev, ...uploadedFiles]);
      setTimeout(() => setUploadProgress(0), 1500);
      
    } catch (error) {
      addNotification({
        type: 'error',
        message: error.message || 'Failed to upload images',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (file.size > 100 * 1024 * 1024) {
      addNotification({
        type: 'warning',
        message: 'Video file is too large. Maximum size is 100MB.',
      });
      return;
    }
    
    if (!file.type.startsWith('video/')) {
      addNotification({
        type: 'error',
        message: 'Please select a valid video file.',
      });
      return;
    }
    
    try {
      setLoading(true);
      const key = `posts/${user.username}/videos/${uuidv4()}-${file.name}`;
      const accessLevel = visibility === 'PUBLIC' ? 'public' : 'protected';
      const uploadedKey = await uploadFile(file, key, (progress) => {
        setUploadProgress(progress);
      }, accessLevel);
      const accessibleUrl = await getFileUrl(uploadedKey, null, accessLevel);
      
      setVideo({
        key: uploadedKey,
        url: accessibleUrl,
        name: file.name,
        size: file.size,
        existing: false,
      });
      
      setTimeout(() => setUploadProgress(0), 1500);
      
    } catch (error) {
      addNotification({
        type: 'error',
        message: error.message || 'Failed to upload video.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddLink = () => {
    if (linkInput.trim()) {
      try {
        if (links.length >= 3) {
          addNotification({
            type: 'warning',
            message: 'Maximum 3 links allowed per post',
          });
          return;
        }
        
        const url = new URL(linkInput);
        if (links.some(link => link === url.href)) {
          addNotification({
            type: 'warning',
            message: 'Link already added',
          });
          return;
        }
        
        setLinks(prev => [...prev, url.href]);
        setLinkInput('');
        setLinkDialogOpen(false);
      } catch (error) {
        addNotification({
          type: 'error',
          message: 'Please enter a valid URL',
        });
      }
    }
  };

  const extractHashtags = (text) => {
    const hashtagPattern = /#[a-zA-Z0-9_]+/g;
    const matches = text.match(hashtagPattern);
    return matches ? matches.map(tag => tag.slice(1)) : [];
  };

  const handleContentChange = (event) => {
    const newContent = event.target.value;
    setContent(newContent);
    
    // Auto-extract hashtags
    const extractedHashtags = extractHashtags(newContent);
    setHashtags(extractedHashtags);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!content.trim() && images.length === 0 && !video && links.length === 0) {
      addNotification({
        type: 'warning',
        message: 'Please add some content to your post',
      });
      textFieldRef.current?.focus();
      return;
    }

    try {
      setLoading(true);
      
      // Clean up old media files that were removed
      const existingImages = post.images || [];
      const currentImageKeys = images.map(img => img.key);
      const removedImages = existingImages.filter(key => !currentImageKeys.includes(key));
      
      // Delete removed images
      for (const key of removedImages) {
        try {
          const accessLevel = post.visibility === 'PUBLIC' ? 'public' : 'protected';
          await deleteFile(key, accessLevel);
        } catch (error) {
          console.warn('Failed to delete old image:', key, error);
        }
      }
      
      // Delete old video if changed
      if (post.video && (!video || video.key !== post.video)) {
        try {
          const accessLevel = post.visibility === 'PUBLIC' ? 'public' : 'protected';
          await deleteFile(post.video, accessLevel);
        } catch (error) {
          console.warn('Failed to delete old video:', post.video, error);
        }
      }
      
      const postData = {
        id: post.id,
        content: content.trim(),
        images: images.length > 0 ? images.map(img => img.key) : null,
        video: video?.key || null,
        links: links.length > 0 ? links : null,
        hashtags: hashtags.length > 0 ? hashtags : null,
        visibility: visibility || 'FOLLOWERS',
      };
      
      const updatedPost = await updatePost(postData);

      const postWithAuthor = {
        ...updatedPost,
        __typename: "Post",
        author: {
          ...user,
          username: user.username,
          verified: user.verified,
        },
        likes: post.likes || { items: [] },
        comments: post.comments || { items: [] },
      };
      
      onPostUpdated(postWithAuthor);
      onClose();

      addNotification({
        type: 'success',
        message: 'Post updated successfully!',
      });
    } catch (error) {
      let errorMsg = 'Failed to update post';
      if (error?.errors && Array.isArray(error.errors) && error.errors.length > 0) {
        errorMsg = error.errors[0].message || errorMsg;
      } else if (error?.message) {
        errorMsg = error.message;
      }
      console.error('EditPost: Error updating post', error);
      addNotification({
        type: 'error',
        message: errorMsg,
      });
    } finally {
      setLoading(false);
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeLink = (index) => {
    setLinks(prev => prev.filter((_, i) => i !== index));
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      addNotification({
        type: 'error',
        message: 'Geolocation is not supported by this browser',
      });
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&addressdetails=1`
          );
          const data = await response.json();
          
          if (data && data.address) {
            const city = data.address.city || data.address.town || data.address.village || data.address.county;
            const country = data.address.country;
            setLocation(city && country ? `${city}, ${country}` : `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
          } else {
            setLocation(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
          }
          
          addNotification({
            type: 'success',
            message: 'Location added',
          });
        } catch (error) {
          setLocation(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
          addNotification({
            type: 'success',
            message: 'Location added (coordinates)',
          });
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        setLoading(false);
        addNotification({
          type: 'error',
          message: 'Unable to get your location',
        });
      }
    );
  };

  const handleClose = () => {
    // Reset form
    setContent('');
    setImages([]);
    setVideo(null);
    setLinks([]);
    setHashtags([]);
    setVisibility('FOLLOWERS');
    setLocation('');
    setMediaUrls([]);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(18, 18, 18, 0.95)' 
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 4px 20px -2px rgba(0, 0, 0, 0.3)'
            : '0 2px 12px -4px rgba(0, 0, 0, 0.15)',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <S3Avatar 
            s3Key={user?.avatar}
            alt={user?.displayName}
            sx={{ width: 48, height: 48 }}
          >
            {user?.displayName?.[0]?.toUpperCase()}
          </S3Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Edit Post
            </Typography>
            <Typography variant="caption" color="text.secondary">
              @{user?.username}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {uploadProgress > 0 && (
          <LinearProgress 
            variant="determinate" 
            value={uploadProgress} 
            sx={{ height: 3, mb: 2, borderRadius: 1.5 }}
          />
        )}

        {/* Visibility Selector */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Who can see this post?
          </Typography>
          <Box display="flex" gap={1} mt={1}>
            {visibilityOptions.map((option) => (
              <Tooltip key={option.value} title={`${option.label} - ${option.description}`}>
                <IconButton
                  size="small"
                  onClick={() => setVisibility(option.value)}
                  color={visibility === option.value ? 'primary' : 'default'}
                  sx={{ 
                    background: visibility === option.value 
                      ? theme.palette.mode === 'dark'
                        ? 'rgba(25, 118, 210, 0.2)'
                        : 'rgba(25, 118, 210, 0.1)'
                      : 'transparent',
                    backdropFilter: visibility === option.value ? 'blur(10px)' : 'none',
                    border: visibility === option.value 
                      ? theme.palette.mode === 'dark'
                        ? '1px solid rgba(25, 118, 210, 0.4)'
                        : '1px solid rgba(25, 118, 210, 0.3)'
                      : '1px solid transparent',
                    borderRadius: 2,
                    mx: 0.5,
                  }}
                >
                  {option.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        </Box>

        <TextField
          ref={textFieldRef}
          fullWidth
          multiline
          rows={4}
          placeholder="What's happening?"
          value={content}
          onChange={handleContentChange}
          variant="outlined"
          disabled={loading}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.02)',
              backdropFilter: 'blur(10px)',
            },
            '& .MuiInputBase-input': {
              fontSize: '1.1rem',
              lineHeight: 1.5,
            }
          }}
        />

        {location && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOn fontSize="small" color="action" />
            <TextField
              fullWidth
              size="small"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Add location..."
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={() => setLocation('')}>
                    <Close fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Box>
        )}

        {hashtags.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Hashtags:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {hashtags.map((tag, index) => (
                <Chip
                  key={index}
                  label={`#${tag}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {images.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Images ({images.length}/5) - Max 10MB each:
            </Typography>
            <Grid container spacing={1}>
              {images.map((image, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Box position="relative">
                    <Paper
                      elevation={2}
                      sx={{
                        position: 'relative',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={image.url}
                        alt={`Upload ${index + 1}`}
                        style={{
                          width: '100%',
                          height: 120,
                          objectFit: 'cover',
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeImage(index)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'rgba(0, 0, 0, 0.6)',
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Paper>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {image.name} {image.existing && '(existing)'}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {video && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Video - Max 100MB:
            </Typography>
            <Box position="relative">
              <video
                src={video.url}
                controls
                style={{
                  width: '100%',
                  maxHeight: 300,
                  borderRadius: 8,
                }}
              />
              <IconButton
                size="small"
                onClick={() => setVideo(null)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {video.name} {video.existing && '(existing)'} {video.size && `(${(video.size / (1024 * 1024)).toFixed(1)} MB)`}
            </Typography>
          </Box>
        )}

        {links.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Links ({links.length}/3):
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {links.map((link, index) => (
                <Chip
                  key={index}
                  label={new URL(link).hostname}
                  size="small"
                  onDelete={() => removeLink(index)}
                  onClick={() => window.open(link, '_blank')}
                  color="info"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Action Buttons */}
        <Box display="flex" gap={{ xs: 0.5, sm: 1 }} flexWrap="wrap" sx={{ mb: 2 }}>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            multiple
            onChange={handleImageUpload}
          />
          <Tooltip title="Add photos">
            <IconButton
              size={isMobile ? "medium" : "small"}
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              sx={iconButtonStyle}
            >
              <Photo fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
          </Tooltip>

          <input
            type="file"
            ref={videoInputRef}
            style={{ display: 'none' }}
            accept="video/*"
            onChange={handleVideoUpload}
          />
          <Tooltip title="Add video">
            <IconButton
              size={isMobile ? "medium" : "small"}
              onClick={() => videoInputRef.current?.click()}
              disabled={loading || !!video}
              sx={iconButtonStyle}
            >
              <Videocam fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Add link">
            <IconButton
              size={isMobile ? "medium" : "small"}
              onClick={() => setLinkDialogOpen(true)}
              disabled={loading}
              sx={iconButtonStyle}
            >
              <LinkIcon fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Add location">
            <IconButton
              size={isMobile ? "medium" : "small"}
              onClick={getCurrentLocation}
              disabled={loading}
              sx={iconButtonStyle}
            >
              <LocationOn fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Add emoji">
            <IconButton
              size={isMobile ? "medium" : "small"}
              onClick={() => setEmojiPickerOpen(true)}
              disabled={loading}
              sx={iconButtonStyle}
            >
              <EmojiEmotions fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Box display="flex" gap={1} alignItems="center" width="100%" justifyContent="space-between">
          <Typography 
            variant="caption" 
            color={content.length > 280 ? 'error' : 'text.secondary'}
          >
            {content.length}/280
          </Typography>
          
          <Box display="flex" gap={1}>
            <Button
              onClick={handleClose}
              disabled={loading}
              sx={{
                color: theme.palette.mode === 'dark' ? '#fff' : '#666',
                borderColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.3)' 
                  : 'rgba(0, 0, 0, 0.23)',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                  borderColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.5)'
                    : 'rgba(0, 0, 0, 0.4)',
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                loading || 
                (!content.trim() && images.length === 0 && !video && links.length === 0) ||
                content.length > 280
              }
              sx={{
                backgroundColor: '#1976d2',
                color: '#fff',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(25, 118, 210, 0.3)'
                  : '0 2px 8px rgba(25, 118, 210, 0.2)',
                '&:hover': {
                  backgroundColor: '#1565c0',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 12px rgba(25, 118, 210, 0.4)'
                    : '0 4px 12px rgba(25, 118, 210, 0.3)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#e0e0e0',
                  color: theme.palette.mode === 'dark' ? '#666' : '#999',
                  boxShadow: 'none',
                }
              }}
            >
              {loading ? 'Updating...' : 'Update Post'}
            </Button>
          </Box>
        </Box>
      </DialogActions>

      {/* Link Dialog */}
      <Dialog 
        open={linkDialogOpen} 
        onClose={() => setLinkDialogOpen(false)}
        PaperProps={{
          sx: {
            background: theme.palette.mode === 'dark'
              ? 'rgba(18, 18, 18, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.08)',
          }
        }}
      >
        <DialogTitle>Add Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            placeholder="https://example.com"
            value={linkInput}
            onChange={(e) => setLinkInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddLink()}
            sx={{ 
              mt: 1,
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.02)',
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddLink} variant="contained">Add Link</Button>
        </DialogActions>
      </Dialog>

      {/* Emoji Picker Dialog */}
      <Dialog 
        open={emojiPickerOpen} 
        onClose={() => setEmojiPickerOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: theme.palette.mode === 'dark'
              ? 'rgba(18, 18, 18, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.08)',
          }
        }}
      >
        <DialogTitle>Choose Emoji</DialogTitle>
        <DialogContent sx={{ maxHeight: 400, overflow: 'auto' }}>
          {Object.entries(emojiCategories).map(([category, emojis]) => (
            <Box key={category} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
                {category}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {emojis.map((emoji, index) => (
                  <Button
                    key={index}
                    onClick={() => {
                      setContent(prev => prev + emoji);
                      setEmojiPickerOpen(false);
                    }}
                    sx={{
                      minWidth: 40,
                      minHeight: 40,
                      fontSize: '1.2rem',
                      p: 0.5,
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(0, 0, 0, 0.05)',
                        transform: 'scale(1.2)',
                      }
                    }}
                  >
                    {emoji}
                  </Button>
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmojiPickerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default EditPost;